<template>
  <div class="trials-list">
    <Breadcrumbs :items="breadcrumbs" />
    <rp-trialtable v-if="loaded && rows.length > 0"
      :rows="rows"
      :pageInfo="pageInfo"
      :surgeonId="surgeonId"
      @getTrials="getTrials"
      @onView="onView"
      @onExport="onExport"
      @onViewResponses="onViewResponses"
      @onRemove="onRemove" />
    <div class="surgeon-warning panel"
      v-if="loaded && rows.length === 0">
      <span>Oops! It seems you aren't currently enrolled on a study that supports RayPRO Clinical.
        If you have a study that you want to use this platform for, or want more information on how
        you can use it, please contact <a href="mailto:digitalhealth@rayner.com">digitalhealth@rayner.com</a>.
      </span>
    </div>

    <rp-button v-if="loaded && isAdmin" 
      class="addbtn" 
      @click="onAddToStudy">
      Add surgeon to active study
    </rp-button>

    <rp-overlay :active="showAddToStudy">
      <rp-addtrialsurgeon
        :trials="openTrials"
        @submit="submitSurgeon"
        @cancel="cancelSurgeon" />
    </rp-overlay>

    <rp-overlay :active="showConfirm">
      <rp-confirm
        icon="warning_rounded"
        :messageText="confirmMessage"
        :confirm="doRemove"
        :cancel="closeConfirm"
      />
    </rp-overlay>
  </div>
</template>

<script>
import page from '@/mixins/page'
import Breadcrumbs from '@/components/Breadcrumbs'
import TrialTable from 'raypro_ui/src/components/ui/TrialTable'
import AddTrialSurgeon from 'raypro_ui/src/components/ui/forms/AddTrialSurgeon'
import Overlay from 'raypro_ui/src/components/ui/Overlay'
import Confirm from 'raypro_ui/src/components/ui/DeleteConfirm'
import Button from 'raypro_ui/src/components/ui/inputs/Button'
import {getUser} from 'raypro_ui/src/api/users'
import {
  getTrialsForSurgeon,
  createTrialSurgeon,
  deleteTrialSurgeon,
  getOpenTrials,
  exportTrialCsv
} from 'raypro_ui/src/api/trials'
import {mapGetters} from 'vuex'

export default {
  mixins: [page],

  components: {
    'rp-trialtable': TrialTable,
    'rp-addtrialsurgeon': AddTrialSurgeon,
    'rp-overlay': Overlay,
    'rp-confirm': Confirm,
    'rp-button': Button,
    Breadcrumbs
  },

  data() {
    return {
      rows: [],
      pageInfo: {
        page_size: 10,
        page_number: 1
      },
      surgeon: {},
      title: 'Clinical Study Profile',
      breadcrumbs: [
        {label: 'Clinical Study Profile'}
      ],
      loaded: false,
      showConfirm: false,
      confirmMessage: '',
      delTrialId: '',
      delSurgeonId: '',
      showAddToStudy: false,
      openTrials: [],
      addSurgeon: {}
    }
  },

  computed: {
    ...mapGetters(['authUser', 'isAdmin']),

    surgeonId() {
      return this.$route.params.id
    },
  },

  async mounted() {
    await this.getUser()
    await this.getTrials(this.pageInfo)
    this.breadcrumbs[0].label = 
      `${this.surgeon.profile.first_name} ${this.surgeon.profile.last_name}'s Clinical Study Profile`
    this.loaded = true
  },

  methods: {
    async getUser() {
      let {data} = await getUser(this.surgeonId)
      this.surgeon = data
    },

    async getTrials(pageInfo) {
      let {data, page} = await getTrialsForSurgeon(this.surgeonId, pageInfo)
      this.rows = data
      this.pageInfo = page
    },

    async getOpenTrials() {
      let {data} = await getOpenTrials()
      this.openTrials = data
    },

    onView(row) {
      this.$router.push({name: 'view-study', params: {id: row.id}})
    },

    async onExport(row) {
      const data = await exportTrialCsv(row.id, this.surgeonId)
      this.downloadResult(data)
    },

    onViewResponses(row, surgeonId) {
      this.$router.push({name: 'view-surgeon-responses', params: {id: row.id, surgeonId: surgeonId}})
    },

    onRemove(row, surgeonId) {
      this.delTrialId = row.id
      this.delSurgeonId = surgeonId
      this.confirmMessage = `Are you sure you want to remove ${this.surgeon.profile.first_name} \
                            ${this.surgeon.profile.last_name} from study ${row.name}?`
      this.showConfirm = true
    },

    async doRemove() {
      this.showConfirm = false
      await deleteTrialSurgeon(this.delTrialId, this.delSurgeonId)
      await this.getTrials(this.pageInfo)
    },

    async onAddToStudy() {
      await this.getOpenTrials()
      this.showAddToStudy = true
    },

    async submitSurgeon(val) {
      this.showAddToStudy = false
      await createTrialSurgeon(val.trial_id, {trial_surgeon: {surgeon_id: this.surgeonId, enrolment_target: val.enrolment_target}})
      await this.getTrials(this.pageInfo)
    },

    cancelSurgeon() {
      this.showAddToStudy = false
    },

    closeConfirm() {
      this.showConfirm = false
    },

    downloadResult(result) {
      const disposition = result.headers['content-disposition']
      const matches = /"([^"]*)"/.exec(disposition)
      const filename = matches !== null && matches[1] ? matches[1] : 'file.csv'
      const blob = new Blob([result.data], {type: 'text/csv'})
      const link = document.createElement('a')

      link.href = window.URL.createObjectURL(blob)
      link.download = filename
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    },
  },
}
</script>

<style lang="sass" scoped>
  @import 'raypro_ui/src/styles/base/_variables.sass'
  @import 'raypro_ui/src/styles/helpers/_mixins.sass'

  .surgeon-warning
    padding: 15px
    font-size: 1.8em
    font-weight: bold
    color: red

    a
      color: red

  .panel
    background-color: $color-panel-bg
    border-radius: 20px
    margin-bottom: 15px

  .addbtn
    position: relative
    top: -60px
    left: 10px
</style>



// WEBPACK FOOTER //
// src/components/pages/Clinical/TrialProfile.vue